<script>
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    NextButton,
  },
  props: {
    knowledgebaseId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      searchQuery: '',
      retrievedDocuments: [],
      searchStrategy: 'vsearch',
      uiFlags: {
        isSearching: false,
      },
    };
  },
  computed: {
    searchStrategyOptions() {
      return [
        {
          value: 'vsearch',
          label: this.$t('LIBRARIES.SEARCH.STRATEGY.SEMANTIC'),
          description: this.$t(
            'LIBRARIES.SEARCH.STRATEGY.SEMANTIC_DESCRIPTION'
          ),
        },
        {
          value: 'rsearch',
          label: this.$t('LIBRARIES.SEARCH.STRATEGY.RETRIEVAL'),
          description: this.$t(
            'LIBRARIES.SEARCH.STRATEGY.RETRIEVAL_DESCRIPTION'
          ),
        },
        {
          value: 'hsearch',
          label: this.$t('LIBRARIES.SEARCH.STRATEGY.HYBRID'),
          description: this.$t('LIBRARIES.SEARCH.STRATEGY.HYBRID_DESCRIPTION'),
        },
        {
          value: 'ksearch',
          label: this.$t('LIBRARIES.SEARCH.STRATEGY.KEYWORD'),
          description: this.$t('LIBRARIES.SEARCH.STRATEGY.KEYWORD_DESCRIPTION'),
        },
      ];
    },
  },
  methods: {
    async searchDocuments() {
      this.uiFlags.isSearching = true;
      try {
        const data = await this.$store.dispatch(
          'knowledgebase/searchDocumentsAction',
          {
            id: this.knowledgebaseId,
            query: this.searchQuery,
            searchType: this.searchStrategy,
          }
        );

        // Handle the new v2 API response structure
        this.retrievedDocuments = data.documents || data || [];
      } catch (error) {
        this.retrievedDocuments = [];
      } finally {
        this.uiFlags.isSearching = false;
      }
    },
  },
};
</script>

<template>
  <div class="flex-1 h-full flex flex-col pt-6">
    <div class="flex-1 h-full flex flex-col">
      <div
        class="bg-slate-25 dark:bg-slate-900 pt-4 pb-0 px-8 border-b border-solid border-slate-50 dark:border-slate-800/50 flex flex-row"
      >
        <h2
          class="text-2xl text-slate-800 dark:text-slate-100 mb-1 font-medium"
        >
          Search Documents
        </h2>
      </div>
      <div class="p-4">
        <form @submit.prevent="searchDocuments">
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              {{ $t('LIBRARIES.SEARCH.STRATEGY.LABEL') }}
            </label>
            <select
              v-model="searchStrategy"
              data-testid="search-strategy-select"
              class="w-full p-2 border border-gray-300 rounded-md"
            >
              <option
                v-for="option in searchStrategyOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }} - {{ option.description }}
              </option>
            </select>
          </div>
          <input
            v-model="searchQuery"
            data-testid="search-query-input"
            type="text"
            :placeholder="$t('LIBRARIES.SEARCH.PLACEHOLDER')"
            class="w-full mb-4 p-2 border border-gray-300 rounded-md"
          />
          <div class="flex flex-row justify-end gap-2 py-2 px-0 w-full">
            <NextButton
              data-testid="search-submit-button"
              :is-loading="uiFlags.isSearching"
              :label="$t('GENERAL_SETTINGS.FORM.SEARCH')"
            />
          </div>
        </form>
        <div
          v-if="retrievedDocuments.length"
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          data-testid="search-results-grid"
        >
          <div
            v-for="(document, index) in retrievedDocuments"
            :key="index"
            class="border p-4 rounded shadow overflow-scroll"
          >
            <pre class="bg-gray-100 p-2 rounded">{{
              document.page_content
            }}</pre>
            <dl class="mt-2">
              <div
                v-for="(value, key) in document.metadata"
                :key="key"
                class="flex"
              >
                <dt class="font-semibold">{{ key }}:</dt>
                <dd class="ml-2">{{ value }}</dd>
              </div>
            </dl>
          </div>
        </div>
        <div v-else-if="uiFlags.isSearching" class="text-blue-500">
          Searching...
        </div>
        <div v-else class="text-red-500">No documents found</div>
      </div>
    </div>
  </div>
</template>
